import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Steps,
  Form,
  Input,
  Select,
  Radio,
  Upload,
  Space,
  Typography,
  Alert,
  Button,
} from 'antd';
import {
  FileTextOutlined,
  UploadOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import {
  useCreateTemplateMutation,
  useImportPreviewMutation,
  useImportCSVMutation,
  useAiAssistMutation,
} from '../../../store/api/templateApi';
import { toast } from '../../../components/common/Toast';
import FieldTypeMapper from './FieldTypeMapper';

const { TextArea } = Input;
const { Title, Paragraph } = Typography;

export default function TemplateWizard({ open, onClose }) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [creationMethod, setCreationMethod] = useState('blank'); // blank, import, ai

  const [createTemplate, { isLoading: isCreating }] = useCreateTemplateMutation();
  const [importPreview, { isLoading: isPreviewing }] = useImportPreviewMutation();
  const [importCSV, { isLoading: isImporting }] = useImportCSVMutation();
  const [aiAssist, { isLoading: isGenerating }] = useAiAssistMutation();

  const [previewData, setPreviewData] = useState(null);
  const [mappedSheets, setMappedSheets] = useState(null);
  const [isFieldMappingValid, setIsFieldMappingValid] = useState(false);

  // Reset form when modal opens
  React.useEffect(() => {
    if (open) {
      form.resetFields();
      setCurrentStep(0);
      setCreationMethod('blank');
      setPreviewData(null);
      setMappedSheets(null);
      setIsFieldMappingValid(false);
    }
  }, [open, form]);

  const getSteps = () => {
    const baseSteps = ['Template Info', 'Creation Method', 'Configure'];
    if (creationMethod === 'import' && previewData) {
      return ['Template Info', 'Creation Method', 'Configure', 'Field Types'];
    }
    return baseSteps;
  };

  const steps = getSteps();

  const templateTypes = [
    { label: 'RFP (Request for Proposal)', value: 'rfp' },
    { label: 'RFI (Request for Information)', value: 'rfi' },
    { label: 'RFQ (Request for Quote)', value: 'rfq' },
    { label: 'Survey', value: 'survey' },
    { label: 'Assessment', value: 'assessment' },
    { label: 'Custom', value: 'custom' },
  ];

  const handleFinish = async () => {
    try {
      // Get all form values (including from previous steps)
      const values = form.getFieldsValue();
      console.log('[Create] Form values:', values);
      console.log('[Create] Creation method:', creationMethod);
      console.log('[Create] Mapped sheets:', mappedSheets);

      // Validate required fields manually
      if (!values.name || !values.type) {
        console.error('[Create] Missing required fields:', { name: values.name, type: values.type });
        toast.error('Please fill in all required fields');
        return;
      }

      let templateData = { sheets: [] };

      if (creationMethod === 'blank') {
        console.log('[Create] Creating blank template');
        // Create blank template with default structure
        templateData = {
          sheets: [
            {
              id: 'sheet_1',
              name: 'Main',
              sections: [],
            },
          ],
        };
      } else if (creationMethod === 'import' && mappedSheets) {
        console.log('[Create] Importing template with mapped sheets');
        console.log('[Create] Mapped sheets data:', JSON.stringify(mappedSheets, null, 2));

        // Import from CSV/Excel with mapped field types
        const result = await importCSV({
          name: values.name,
          type: values.type,
          sheets: mappedSheets,
          source: previewData?.source || 'import',
          filename: previewData?.filename || 'unknown'
        }).unwrap();

        console.log('[Create] Import successful, template ID:', result.id);
        console.log('[Create] Import result:', result);
        toast.success('Template imported');
        navigate(`/library/builder/${result.id}`);
        onClose();
        return;
      } else if (creationMethod === 'ai' && values.aiPrompt) {
        console.log('[Create] Generating template with AI');
        // Generate with AI (create template first, then generate structure)
        const template = await createTemplate({
          name: values.name,
          type: values.type,
          description: values.description,
          template_data: { sheets: [] },
        }).unwrap();

        // Generate structure with AI
        try {
          const aiResult = await aiAssist({
            id: template.id,
            prompt: values.aiPrompt,
          }).unwrap();

          toast.success('Template generated with AI');
          navigate(`/library/builder/${template.template?.id || template.id}`);
          onClose();
          return;
        } catch (aiError) {
          console.error('AI generation error:', aiError);
          // Still navigate to builder if AI fails
          toast.warning('Template created', 'AI generation failed');
          navigate(`/library/builder/${template.template?.id || template.id}`);
          onClose();
          return;
        }
      } else if (creationMethod === 'import') {
        console.error('[Create] Import method selected but no mapped sheets available');
        toast.error('No import data available. Please go back and upload a file.');
        return;
      }

      // Create template (fallback for blank or unhandled cases)
      console.log('[Create] Creating template with data:', templateData);
      const result = await createTemplate({
        name: values.name,
        type: values.type,
        description: values.description,
        template_data: templateData,
      }).unwrap();

      console.log('[Create] Template created, ID:', result.template?.id || result.id);
      toast.success('Template created');
      navigate(`/library/builder/${result.template?.id || result.id}`);
      onClose();
    } catch (error) {
      console.error('Template creation error:', error);
      toast.error('Failed to create template');
    }
  };

  const renderStepContent = () => {
    return (
      <>
        {/* Step 0: Template Info - Always mounted, hidden when not active */}
        <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Form.Item
              name="name"
              label="Template Name"
              rules={[{ required: true, message: 'Please enter template name' }]}
            >
              <Input
                placeholder="e.g., Security Assessment RFP"
                size="large"
                prefix={<FileTextOutlined />}
              />
            </Form.Item>

            <Form.Item
              name="type"
              label="Template Type"
              rules={[{ required: true, message: 'Please select template type' }]}
              initialValue="rfp"
            >
              <Select size="large" options={templateTypes} />
            </Form.Item>

            <Form.Item name="description" label="Description (Optional)">
              <TextArea
                rows={4}
                placeholder="Brief description of this template's purpose"
              />
            </Form.Item>
          </Space>
        </div>

        {/* Step 1: Creation Method - Always mounted, hidden when not active */}
        <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Title level={5}>How would you like to create this template?</Title>

            <Radio.Group
              value={creationMethod}
              onChange={(e) => setCreationMethod(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Radio value="blank" style={{ padding: '16px', border: '1px solid #d9d9d9', borderRadius: '8px', width: '100%' }}>
                  <Space direction="vertical" size={0}>
                    <strong>Start from Blank</strong>
                    <Paragraph type="secondary" style={{ margin: 0, fontSize: '13px' }}>
                      Build your template from scratch using drag-and-drop
                    </Paragraph>
                  </Space>
                </Radio>

                <Radio value="import" style={{ padding: '16px', border: '1px solid #d9d9d9', borderRadius: '8px', width: '100%' }}>
                  <Space direction="vertical" size={0}>
                    <strong>Import from CSV/Excel</strong>
                    <Paragraph type="secondary" style={{ margin: 0, fontSize: '13px' }}>
                      Upload a CSV or Excel file - columns become fields with auto-detected types
                    </Paragraph>
                  </Space>
                </Radio>

                <Radio value="ai" style={{ padding: '16px', border: '1px solid #d9d9d9', borderRadius: '8px', width: '100%' }}>
                  <Space direction="vertical" size={0}>
                    <strong>Generate with AI</strong>
                    <Paragraph type="secondary" style={{ margin: 0, fontSize: '13px' }}>
                      Describe your needs and let AI create the structure
                    </Paragraph>
                  </Space>
                </Radio>
              </Space>
            </Radio.Group>
          </Space>
        </div>

        {/* Step 2: Configure - Always mounted, hidden when not active */}
        <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {creationMethod === 'blank' && (
              <Alert
                message="Ready to Build"
                description="You'll be taken to the template builder where you can drag and drop components to create your template structure."
                type="info"
                showIcon
              />
            )}

            {creationMethod === 'import' && (
              <Form.Item
                name="csvFile"
                label="Upload File"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e?.fileList;
                }}
                rules={[{ required: true, message: 'Please upload a file' }]}
              >
                <Upload
                  accept=".csv,.xlsx,.xls"
                  maxCount={1}
                  beforeUpload={() => false}
                >
                  <button
                    type="button"
                    style={{
                      padding: '12px 24px',
                      border: '1px dashed #8B5CF6',
                      borderRadius: '8px',
                      background: '#F9F5FF',
                      cursor: 'pointer',
                    }}
                  >
                    <Space>
                      <UploadOutlined />
                      Click to Upload CSV or Excel File
                    </Space>
                  </button>
                </Upload>
              </Form.Item>
            )}

            {creationMethod === 'ai' && (
              <Form.Item
                name="aiPrompt"
                label="Describe Your Template"
                rules={[{ required: true, message: 'Please describe the template' }]}
              >
                <TextArea
                  rows={6}
                  placeholder="Example: Create a security assessment RFP with sections for company info, technical requirements, security compliance, and pricing. Include questions about certifications, data protection, and incident response."
                  prefix={<RobotOutlined />}
                />
              </Form.Item>
            )}
          </Space>
        </div>

        {/* Step 3: Field Type Mapping (only for import) */}
        {creationMethod === 'import' && previewData && (
          <div style={{ display: currentStep === 3 ? 'block' : 'none' }}>
            <FieldTypeMapper
              sheets={mappedSheets || previewData.sheets}
              onChange={setMappedSheets}
              onValidate={setIsFieldMappingValid}
            />
          </div>
        )}
      </>
    );
  };

  const handleNext = async () => {
    // Special handling for import file upload step
    if (currentStep === 2 && creationMethod === 'import') {
      const values = form.getFieldsValue();
      console.log('[Import] Form values:', values);
      console.log('[Import] csvFile value:', values.csvFile);

      // With valuePropName="fileList", values.csvFile is the fileList array directly
      if (!values.csvFile || !Array.isArray(values.csvFile) || values.csvFile.length === 0) {
        console.error('[Import] No file in fileList:', values.csvFile);
        toast.error('Please upload a file');
        return;
      }

      try {
        // Get the actual file from Upload component
        const file = values.csvFile[0].originFileObj;
        console.log('[Import] Uploading file:', file?.name);

        if (!file) {
          console.error('[Import] No originFileObj found:', values.csvFile[0]);
          toast.error('File not found. Please try uploading again.');
          return;
        }

        // Upload file and get preview
        const formData = new FormData();
        formData.append('file', file);

        const result = await importPreview(formData).unwrap();
        console.log('[Import] Preview result:', result);

        setPreviewData({
          sheets: result.sheets,
          filename: result.filename,
          source: result.filename?.endsWith('.csv') ? 'csv' : 'xlsx'
        });
        setMappedSheets(result.sheets);
        setCurrentStep(currentStep + 1);
      } catch (error) {
        console.error('[Import] Preview error:', error);
        toast.error('Failed to preview file: ' + (error?.data?.error || error.message));
      }
      return;
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getNextButtonProps = () => {
    const isImportFileStep = currentStep === 2 && creationMethod === 'import';
    const isFieldMappingStep = currentStep === 3 && creationMethod === 'import';
    const isLastStep = currentStep === steps.length - 1;

    // Check import file step BEFORE checking last step
    // because steps.length changes after preview
    if (isImportFileStep && !previewData) {
      return {
        loading: isPreviewing,
        disabled: false,
        children: 'Preview & Map Fields'
      };
    }

    return {
      loading: isCreating || isImporting || isGenerating || isPreviewing,
      disabled: isFieldMappingStep && !isFieldMappingValid,
      children: isLastStep ? 'Create' : 'Next'
    };
  };

  return (
    <Modal
      title="Create New Template"
      open={open}
      onCancel={onClose}
      width={creationMethod === 'import' && previewData ? 900 : 700}
      footer={[
        <Button key="back" onClick={currentStep === 0 ? onClose : handleBack}>
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={() => {
            // Special case: import file upload step before preview
            if (currentStep === 2 && creationMethod === 'import' && !previewData) {
              handleNext();
            } else if (currentStep === steps.length - 1) {
              handleFinish();
            } else {
              handleNext();
            }
          }}
          style={{ background: '#8B5CF6', borderColor: '#8B5CF6' }}
          {...getNextButtonProps()}
        />,
      ]}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Steps current={currentStep} items={steps.map(title => ({ title }))} />

        <Form form={form} layout="vertical" size="large" preserve={true}>
          {renderStepContent()}
        </Form>
      </Space>
    </Modal>
  );
}
